/**
 * i18n module: locale management and string lookup.
 * Self-contained, no external dependencies.
 * Default locale: RU. Falls back to EN for missing keys.
 */

import { commonEn } from "./common/en.js";
import { commonRu } from "./common/ru.js";
import { buddhaEn } from "./campaigns/buddha/en.js";
import { buddhaRu } from "./campaigns/buddha/ru.js";
import { sanctifiedEn } from "./campaigns/sanctified/en.js";
import { sanctifiedRu } from "./campaigns/sanctified/ru.js";

export type Locale = "en" | "ru";
export type I18nCampaignId = "engine" | "engine-v2" | "sanctified" | "buddha";

const commonLocales: Record<Locale, Record<string, string>> = {
  en: commonEn,
  ru: commonRu,
};

const campaignLocales: Record<I18nCampaignId, Partial<Record<Locale, Record<string, string>>>> = {
  engine: {},
  "engine-v2": {},
  sanctified: {
    en: sanctifiedEn,
    ru: sanctifiedRu,
  },
  buddha: {
    en: buddhaEn,
    ru: buddhaRu,
  },
};

const LS_KEY = "gc_locale";

let currentLocale: Locale =
  typeof localStorage !== "undefined" && localStorage.getItem(LS_KEY) === "en"
    ? "en"
    : "ru";
let currentCampaign: I18nCampaignId = "sanctified";

const listeners: Array<(loc: Locale) => void> = [];

export function getLocale(): Locale {
  return currentLocale;
}

export function getI18nCampaign(): I18nCampaignId {
  return currentCampaign;
}

export function setI18nCampaign(campaignId: I18nCampaignId): void {
  currentCampaign = campaignId;
}

export function setLocale(loc: Locale): void {
  if (loc === currentLocale) return;
  currentLocale = loc;
  if (typeof localStorage !== "undefined") localStorage.setItem(LS_KEY, loc);
  for (const l of listeners) l(loc);
}

/**
 * Subscribe to locale changes.
 * Returns an unsubscribe function.
 */
export function onLocaleChange(cb: (loc: Locale) => void): () => void {
  listeners.push(cb);
  return () => {
    const i = listeners.indexOf(cb);
    if (i >= 0) listeners.splice(i, 1);
  };
}

/**
 * Look up a translation key in the current locale.
 * Falls back to EN if the key is missing in the current locale.
 * If not found in EN either, returns the key itself.
 * Supports {{name}} placeholders via the params argument.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  let str = campaignLocales[currentCampaign][currentLocale]?.[key];
  if (str === undefined) str = commonLocales[currentLocale][key];
  if (str === undefined) str = campaignLocales[currentCampaign].en?.[key];
  if (str === undefined) str = commonEn[key];
  if (str === undefined) return key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
    }
  }
  return str;
}
